import { useCallback, useMemo, useState } from "react"
import { getReportColumnDefinitions } from "config/reportColumnDefinitions"

const buildInitialColumns = reportKey => {
  return getReportColumnDefinitions(reportKey).map(column => ({
    ...column,
    visible: column.visible !== false,
  }))
}

export const useReportColumns = reportKey => {
  const [columns, setColumns] = useState(() => buildInitialColumns(reportKey))

  const visibleColumns = useMemo(
    () => columns.filter(column => column.visible),
    [columns]
  )

  const visibleKeys = useMemo(
    () => visibleColumns.map(column => column.key),
    [visibleColumns]
  )

  const toggleColumn = useCallback(key => {
    setColumns(prev =>
      prev.map(column =>
        column.key === key && !column.required
          ? { ...column, visible: !column.visible }
          : column
      )
    )
  }, [])

  const moveColumnToIndex = (key, toIndex) => {
    setColumns(prev => {
      const fromIndex = prev.findIndex(col => col.key === key)
      if (fromIndex === -1 || fromIndex === toIndex) return prev
      const next = [...prev]
      const [removed] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, removed)
      return next
    })
  }

  const moveColumnByKey = (key, direction) => {
    setColumns(prev => {
      const fromIndex = prev.findIndex(col => col.key === key)
      if (fromIndex === -1) {
        return prev
      }
      const toIndex = fromIndex + direction
      if (toIndex < 0 || toIndex >= prev.length) {
        return prev
      }
      const next = [...prev]
      const [removed] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, removed)
      return next
    })
  }

  const resetColumns = useCallback(() => {
    setColumns(buildInitialColumns(reportKey))
  }, [reportKey])

  return {
    columns,
    visibleColumns,
    visibleKeys,
    toggleColumn,
    moveColumnByKey,
    moveColumnToIndex,
    resetColumns,
    setColumns,
  }
}

export default useReportColumns
